import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs';

import { hasSuperUserOrAbove } from '@gfe/core/models/user-role.constants';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { selectUser } from '@gfe/core/store/auth/auth.selectors';
import { selectDataspaceModel } from '@gfe/core/store/dataspace/dataspace.selectors';
import { isNil } from '@gfe/core/utils/shared-utils';
import { DropdownComponent } from '@gfe/shared/components/dropdown/dropdown.component';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';
import { DropdownItemFulfilled } from '@gfe/shared/models/dropdown-item.model';

import {
  RecipeParameterSchema,
  RecipeParameterSubmissionError,
  RecipeParameterValues
} from '@core/models/recipe-parameters.model';
import { RecipeListConfig } from '@core/models/recipes.model';
import { RecipeParametersService } from '@core/services/recipes/recipe-parameters.service';
import { SolverService } from '@core/services/solvers/solvers.service';
import { toRecipeParameterSubmissionError } from '@core/utils/recipe-parameter-error.utils';
import {
  hasParameters,
  initialValues,
  isValid,
  toSubmissionPayload
} from '@core/utils/recipe-parameter-schema.utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { RecipeParameterFormComponent } from '@shared/components/recipe-parameter-form/recipe-parameter-form.component';
import { TagsInputComponent } from '@shared/components/row-edition-form/tags-input/tags-input.component';

export interface NewPlanningSpaceOutputData {
  recipeId: number;
  solverId: number;
  name: string;
  description: string;
  tags: string[];
  /** Absent for a recipe that takes no parameters, keeping the existing flow untouched. */
  parameters?: RecipeParameterValues;
  /** Only ever true when an internal role ticked the box; GBE ignores it from anyone else. */
  isInternal: boolean;
}

export interface NewPlanningSpaceConfig {
  selectedRecipeId: number;
  availableRecipes: RecipeListConfig[];
}

@Component({
  selector: 'app-new-planning-space',
  imports: [
    DropdownComponent,
    TooltipComponent,
    TranslatePipe,
    ReactiveFormsModule,
    RecipeParameterFormComponent,
    CheckboxComponent,
    TagsInputComponent
  ],
  templateUrl: './new-planning-space.component.html',
  styles: ``
})
export class NewPlanningSpaceComponent {
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly solverService = inject(SolverService);
  private readonly recipeParameters = inject(RecipeParametersService);
  private readonly destroyRef = inject(DestroyRef);

  protected isRecipeDropdownOpen = signal<boolean>(false);
  protected isSolverDropdownOpen = signal<boolean>(false);

  closeModal = output();
  newPlanningSpaceOutput = output<NewPlanningSpaceOutputData>();

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  config = input.required<NewPlanningSpaceConfig>();

  /** The raw rejection from the parent; mapped here, where the schema lives. */
  submissionErrorSource = input<unknown>(null);

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
    description: ['', [Validators.maxLength(500)]],
    tags: this.fb.control<string[] | null>([]),
    isInternal: [false]
  });

  private readonly currentUser = this.store.selectSignal(selectUser);

  /** The box is offered only to the roles GBE would honour it from. */
  protected readonly canCreateInternal = computed(() =>
    hasSuperUserOrAbove(this.currentUser()?.role)
  );

  protected readonly setIsInternal = (checked: boolean): void =>
    this.form.controls.isInternal.setValue(checked);

  protected parameterSchema = signal<RecipeParameterSchema | null>(null);
  protected parameterValues = signal<RecipeParameterValues>({});
  protected schemaLoading = signal(false);
  protected schemaFailed = signal(false);
  protected submitAttempted = signal(false);

  protected submissionError = computed<RecipeParameterSubmissionError | null>(() =>
    toRecipeParameterSubmissionError(this.parameterSchema(), this.submissionErrorSource())
  );

  protected selectedRecipeId = signal<number>(0);
  protected selectedSolverId = signal<number>(0);
  protected dataSpace = toSignal(this.store.select(selectDataspaceModel));

  protected solverTypes = signal([{ id: 0, name: '' }]);

  constructor() {
    // Seeding the recipe must not share an effect with `updateDropDownConfig`,
    // which reads `selectedRecipeId`: the write would retrigger the effect and
    // snap the value back to the config's, discarding the planner's own choice.
    effect(() => this.selectedRecipeId.set(this.config().selectedRecipeId));

    effect(() => {
      this.currentLang();
      this.config();
      this.updateDropDownConfig();
    });

    effect(() => {
      const dataSpaceId = this.dataSpace()?.id;
      this.solverService
        .getSolverList$(dataSpaceId)
        .pipe(
          map((solvers) => solvers.map((s) => ({ id: s.id, name: s.name }))),
          take(1)
        )
        .subscribe((solvers) => this.solverTypes.set(solvers));
    });

    effect(() => this.loadParameterSchema(this.selectedRecipeId()));
  }

  /**
   * Fetches the selected recipe's parameter schema. A failure blocks submission
   * rather than silently creating a plan: without the schema there is no way to
   * know whether the recipe requires values.
   */
  private loadParameterSchema(recipeId: number): void {
    this.parameterSchema.set(null);
    this.parameterValues.set({});
    this.submitAttempted.set(false);
    this.schemaFailed.set(false);

    if (!recipeId) {
      this.schemaLoading.set(false);
      return;
    }

    this.schemaLoading.set(true);
    this.recipeParameters
      .getSchemaForRecipe$(recipeId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (schema) => {
          this.parameterSchema.set(schema);
          this.parameterValues.set(initialValues(schema));
          this.schemaLoading.set(false);
        },
        error: () => {
          this.schemaFailed.set(true);
          this.schemaLoading.set(false);
        }
      });
  }

  /** True only when the recipe declares parameters, so no form step appears otherwise. */
  protected showParameterForm(): boolean {
    return hasParameters(this.parameterSchema());
  }

  protected onParameterValuesChange(values: RecipeParameterValues): void {
    this.parameterValues.set(values);
  }

  private updateDropDownConfig(): void {
    this.recipeDropdownLabel.set(this.translate.instant(this.recipeDropdownLabelKey));
    this.recipeDropdownTooltip.set(this.translate.instant(this.recipeDropdownTooltipKey));
    this.availableRecipes.set(
      this.config().availableRecipes.map((r) => ({
        label: r.name,
        isSelected: r.id === this.selectedRecipeId(),
        action: () => {
          this.selectedRecipeId.set(r.id);
          this.updateDropDownConfig();
        }
      })) as DropdownItemFulfilled[]
    );

    this.solverDropdownLabel.set(this.translate.instant(this.solverDropdownLabelKey));
    this.solverDropdownTooltip.set(this.translate.instant(this.solverDropdownTooltipKey));

    this.availableSolvers.set(
      this.solverTypes().map((s) => ({
        label: s.name,
        isSelected: s.id === this.selectedSolverId(),
        action: () => {
          this.selectedSolverId.set(s.id);
          this.updateDropDownConfig();
        }
      })) as DropdownItemFulfilled[]
    );
    if (!this.selectedSolverId() && this.solverTypes().length > 0) {
      this.selectedSolverId.set(this.solverTypes()[0].id);
    }
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isFormValid()) {
      return;
    }

    const schema = this.parameterSchema();
    this.newPlanningSpaceOutput.emit({
      recipeId: this.selectedRecipeId(),
      solverId: this.selectedSolverId(),
      name: this.form.value.name ?? '',
      description: this.form.value.description ?? '',
      tags: this.form.value.tags ?? [],
      // Never emit a tick a hidden box could still be holding from an earlier role read.
      isInternal: this.canCreateInternal() && (this.form.value.isInternal ?? false),
      ...(hasParameters(schema)
        ? { parameters: toSubmissionPayload(schema, this.parameterValues()) }
        : {})
    });
  }

  isFormValid(): boolean {
    if (!this.form.valid || this.schemaLoading() || this.schemaFailed()) return false;
    const schema = this.parameterSchema();
    // Every parameter is required: there are no optional parameters in v1.
    return !hasParameters(schema) || isValid(schema, this.parameterValues());
  }

  availableRecipes = signal<DropdownItemFulfilled[]>([]);
  protected recipeDropdownLabel = signal<string>('');
  protected recipeDropdownTooltip = signal<string>('');
  protected recipeDropdownIconClass = ICONS.scroll;

  availableSolvers = signal<DropdownItemFulfilled[]>([]);
  protected solverDropdownLabel = signal<string>('');
  protected solverDropdownTooltip = signal<string>('');
  protected solverDropdownIconClass = ICONS.brain;

  protected toggleRecipeDropdown(event: Event): void {
    event.preventDefault();
    this.isRecipeDropdownOpen.update((v) => !v);
  }

  protected onCloseRecipeDropdown = (): void => {
    this.isRecipeDropdownOpen.set(false);
  };

  protected toggleSolverDropdown(event: Event): void {
    event.preventDefault();
    this.isSolverDropdownOpen.update((v) => !v);
  }

  protected onCloseSolverDropdown = (): void => {
    this.isSolverDropdownOpen.set(false);
  };

  protected getSelectedRecipeLabel(): string {
    const selected = this.availableRecipes().find((r) => r.isSelected);
    return selected?.label ?? this.recipeDropdownLabel();
  }

  protected getSelectedSolverLabel(): string {
    const selected = this.availableSolvers().find((s) => s.isSelected);
    return selected?.label ?? this.solverDropdownLabel();
  }

  private readonly translationPrefixKey = 'features.planning_space_list.new_from_recipe.';
  protected readonly recipeDropdownLabelKey =
    this.translationPrefixKey + 'recipe_dropdown_label';
  protected readonly recipeDropdownTooltipKey =
    this.translationPrefixKey + 'recipe_dropdown_tooltip';
  protected readonly solverDropdownLabelKey =
    this.translationPrefixKey + 'solver_dropdown_label';
  protected readonly solverDropdownTooltipKey =
    this.translationPrefixKey + 'solver_dropdown_tooltip';
  protected readonly nameKey = 'common.name';
  protected readonly solverKey = this.translationPrefixKey + 'solver';
  protected readonly descriptionKey = 'common.description';
}
